import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { getAuthenticatedUser } from '../../redux/modules/user';
import { logoutUser } from '../../redux/modules/authentication';
import { mobileBreakpoint } from '../../constants/ui-constants';
import { getComponentTranslator } from '../../util/i18n';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faVideo,
  faInfoCircle,
  faSignOutAlt,
  faSignInAlt,
  faUserPlus,
  faBars,
  faTimes,
  faChalkboardTeacher,
  faCaretDown,
  faTrophy
} from '@fortawesome/free-solid-svg-icons';
import styles from './header.module.css';

const translate = getComponentTranslator('header');

class Header extends Component {
  state = {
    isMobile: window.innerWidth <= mobileBreakpoint,
    mobileNavOpen: false,
    activeDropdown: null,
    dropdownTimer: null
  };

  componentDidMount = () => {
    window.addEventListener('resize', this.mobileCheck);
  };

  componentWillUnmount = () => {
    window.removeEventListener('resize', this.mobileCheck);
    // Rensa eventuella timers när komponenten avmonteras
    if (this.state.dropdownTimer) {
      clearTimeout(this.state.dropdownTimer);
    }
  };

  mobileCheck = () => this.setState({ isMobile: window.innerWidth <= mobileBreakpoint });

  handleLogout = async () => {
    try {
      await this.props.logoutUser();
      localStorage.removeItem('token');
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  handleLogoClick = () => {
    this.props.history.push('/dashboard');
    if (this.state.mobileNavOpen) {
      this.setState({ mobileNavOpen: false });
    }
  };

  // Funktion för att hantera hover på dropdown-menyer
  handleDropdownHover = (dropdownName) => {
    // Rensa eventuell befintlig timer
    if (this.state.dropdownTimer) {
      clearTimeout(this.state.dropdownTimer);
    }

    if (!this.state.isMobile) {
      this.setState({
        activeDropdown: dropdownName,
        dropdownTimer: null
      });
    }
  };

  // Funktion för att stänga dropdown-menyer med fördröjning
  handleDropdownLeave = () => {
    if (!this.state.isMobile) {
      // Sätt en timer för att stänga dropdown-menyn efter 500ms
      const timer = setTimeout(() => {
        this.setState({ activeDropdown: null });
      }, 500); // 500ms fördröjning

      this.setState({ dropdownTimer: timer });
    }
  };

  // Funktion för att hantera klick på dropdown-menyer
  handleDropdownClick = (dropdownName, event) => {
    // Förhindra att länken navigerar direkt
    event.preventDefault();

    // Växla dropdown-menyn
    this.setState(prevState => ({
      activeDropdown: prevState.activeDropdown === dropdownName ? null : dropdownName
    }));
  };

  // Funktion för att navigera till Record direkt
  handleRecordClick = () => {
    this.props.history.push('/record');
    this.setState({ activeDropdown: null });
    if (this.state.mobileNavOpen) {
      this.setState({ mobileNavOpen: false });
    }
  };

  buildNavigation = () => {
    const { authenticated } = this.props;
    const { activeDropdown } = this.state;

    // Definiera dropdown-menyer
    const dropdowns = {
      home: [
        {
          name: translate('Record'),
          link: 'record',
          icon: faTrophy,
          authenticated: true,
          onClick: this.handleRecordClick
        }
      ]
    };

    const links = [
      {
        name: translate('Home'),
        link: 'dashboard',
        icon: faHome,
        authenticated: true,
        hasDropdown: true,
        dropdownName: 'home'
      },
      {
        name: translate('Classes'),
        link: 'competitions',
        icon: faChalkboardTeacher,
        authenticated: true,
      },
      // Ta bort Record från huvudmenyn eftersom den nu finns i dropdown
      {
        name: translate('Videos'),
        link: 'videos',
        icon: faVideo,
        authenticated: true,
      },
      {
        name: 'Alumni',
        link: 'alumni',
        icon: faTrophy,
        authenticated: true,
      },
      {
        name: translate('About Us'),
        link: 'about',
        icon: faInfoCircle,
        authenticated: true,
      },
      {
        name: translate('Sign Out'),
        onClick: this.handleLogout,
        icon: faSignOutAlt,
        authenticated: true,
        mobileOnly: true
      },
      {
        name: translate('')
      }
    ];

    return (
      <ul>
        {links
          .filter(link => link.authenticated === authenticated)
          .filter(link => !link.mobileOnly || (link.mobileOnly && this.state.isMobile))
          .map(link => (
            <li
              key={link.name}
              className={`${link.hasDropdown ? styles.hasDropdown : ''} ${activeDropdown === link.dropdownName ? styles.active : ''}`}
              onMouseEnter={() => link.hasDropdown && this.handleDropdownHover(link.dropdownName)}
              onMouseLeave={this.handleDropdownLeave}
            >
              {link.hasDropdown ? (
                <div className={styles.dropdownContainer}>
                  <div
                    className={styles.dropdownTrigger}
                    onClick={(e) => this.handleDropdownClick(link.dropdownName, e)}
                  >
                    <Link to={link.link} className={styles.navLink}>
                      <FontAwesomeIcon icon={link.icon} className={styles.navIcon} />
                      {link.name}
                      <FontAwesomeIcon icon={faCaretDown} className={styles.dropdownIcon} />
                    </Link>
                  </div>

                  {/* Dropdown-meny */}
                  {(activeDropdown === link.dropdownName || (this.state.isMobile && activeDropdown === link.dropdownName)) && (
                    <div
                      className={styles.dropdownMenu}
                      onMouseEnter={() => this.handleDropdownHover(link.dropdownName)}
                      onMouseLeave={this.handleDropdownLeave}
                    >
                      {dropdowns[link.dropdownName].map(dropdownItem => (
                        <div
                          key={dropdownItem.name}
                          className={styles.dropdownItem}
                          onClick={dropdownItem.onClick}
                        >
                          <FontAwesomeIcon icon={dropdownItem.icon} className={styles.navIcon} />
                          {dropdownItem.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : link.link ? (
                <Link to={link.link} className={styles.navLink}>
                  <FontAwesomeIcon icon={link.icon} className={styles.navIcon} />
                  {link.name}
                </Link>
              ) : (
                <button onClick={link.onClick} className={styles.navLink}>
                  <FontAwesomeIcon icon={link.icon} className={styles.navIcon} />
                  {link.name}
                </button>
              )}
            </li>
          ))}
      </ul>
    );
  };

  toggleMobileNav = () => this.setState(prev => ({ mobileNavOpen: !prev.mobileNavOpen }));

  render() {
    const { isMobile, mobileNavOpen } = this.state;

    return (
      <div className={styles.headerContainer}>
        <header className={styles.header}>
          <div className={styles.logo} onClick={this.handleLogoClick}
            style={{ display: 'flex', alignItems: 'center', background: 'none' }}>
            <img
              src="https://i.pinimg.com/originals/d5/96/3c/d5963c6f0bc206e3723f796e3b54fd6b.gif"
              alt="Soccer Ball Logo"
              className={styles.logoImage}
              style={{ width: '50px', height: '50px', marginRight: '10px' }}
            />
            <span className={styles.highlight}>HW</span>
          </div>

          {isMobile && (
            <button
              className={styles.mobileNavToggle}
              onClick={this.toggleMobileNav}
              aria-label="Toggle navigation"
            >
              <FontAwesomeIcon icon={mobileNavOpen ? faTimes : faBars} />
            </button>
          )}

          <nav className={`${styles.mainNavigation} ${mobileNavOpen ? styles.isExpanded : ''}`}>
            {this.buildNavigation()}
          </nav>

          {!isMobile && (
            <div className={styles.authNav}>
              <button onClick={this.handleLogout} className={styles.signOutButton}>
                <FontAwesomeIcon icon={faSignOutAlt} />
                Sign Out
              </button>
            </div>
          )}
        </header>
      </div>
    );
  }
}

Header.propTypes = {
  user: PropTypes.shape({
    firstName: PropTypes.string,
  }),
  authenticated: PropTypes.bool,
  logoutUser: PropTypes.func,
  history: PropTypes.object,
};

const mapStateToProps = ({ user, authentication }) => ({
  user: getAuthenticatedUser({ user, authentication }),
  authenticated: authentication.authenticated,
});

export default connect(mapStateToProps, { logoutUser })(withRouter(Header));
